import React, { useState, useEffect } from 'react'
import azmLogo from '../Images/logo-azm-2.png';
import { Link, useLocation } from 'react-router-dom';
import GeneralService from '../../services/general.service';
import './Navbar.css';

export default function Navbar() {
  const [openingHours, setOpeningHours] = useState('Loading...');
  const [isOpen, setIsOpen] = useState(false);
  const [showMobileNotification, setShowMobileNotification] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [hoursData, setHoursData] = useState(null);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchOpeningHours();

    // Check if open every minute
    const interval = setInterval(checkIfOpen, 60000);

    // Mobile notification timer - show for 4 seconds, hide for 6 seconds
    const notificationInterval = setInterval(() => {
      setShowMobileNotification(prev => !prev);
    }, showMobileNotification ? 4000 : 6000);

    return () => {
      clearInterval(interval);
      clearInterval(notificationInterval);
    };
  }, [showMobileNotification]);

  // Check if open whenever hoursData changes
  useEffect(() => {
    if (hoursData) {
      checkIfOpen();
    }
  }, [hoursData]);

  // Toggle navbar function
  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  // Close navbar when clicking on a link
  const closeNavbar = () => {
    setIsNavbarOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const handleBookAppointment = (e) => {
    closeNavbar();
    if (location.pathname === '/') {
      e.preventDefault();
      document.getElementById('appointment-form')?.scrollIntoView({ behavior: 'smooth' });
    }
    // Otherwise let the link navigate to "/#appointment-form" normally;
    // Home picks up the hash on mount and scrolls to it there.
  };

  const checkIfOpen = () => {
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 60 + currentMinute;

    // If we have API data, use it
    if (hoursData && Array.isArray(hoursData)) {
      let todaySchedule = null;

      // Find today's schedule
      for (const schedule of hoursData) {
        // Check if day_range exists and includes today
        if (schedule.day_range) {
          const days = parseDayRange(schedule.day_range);
          if (days.includes(currentDay)) {
            todaySchedule = schedule;
            break;
          }
        }

        // Check if individual day matches
        if (schedule.day && schedule.day === currentDay) {
          todaySchedule = schedule;
          break;
        }
      }

      if (todaySchedule) {
        // Check if closed
        if (todaySchedule.is_closed) {
          setIsOpen(false);
          return;
        }

        // Check opening hours
        if (todaySchedule.open_time && todaySchedule.close_time) {
          const openTime = timeToMinutes(todaySchedule.open_time);
          const closeTime = timeToMinutes(todaySchedule.close_time);

          const shouldBeOpen = currentTime >= openTime && currentTime < closeTime;
          setIsOpen(shouldBeOpen);
          return;
        }
      } else {
        // If no specific schedule found but we have data,
        // check if there's a general schedule that applies to all days
        const generalSchedule = hoursData.find(schedule =>
          !schedule.day && !schedule.day_range && schedule.open_time && schedule.close_time
        );

        if (generalSchedule) {
          const openTime = timeToMinutes(generalSchedule.open_time);
          const closeTime = timeToMinutes(generalSchedule.close_time);
          const shouldBeOpen = currentTime >= openTime && currentTime < closeTime;
          setIsOpen(shouldBeOpen);
          return;
        }
      }
    }

    // Fallback to default hours if no API data or no matching schedule
    const openTime = 9 * 60; // 9:00 in minutes
    const closeTime = 22 * 60; // 22:00 (10 PM) in minutes

    const shouldBeOpen = currentTime >= openTime && currentTime < closeTime;
    setIsOpen(shouldBeOpen);
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeString) => {
    if (!timeString || typeof timeString !== 'string') {
      return 0;
    }

    try {
      const parts = timeString.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1] || '0', 10);
      // Note: We ignore seconds (parts[2]) as they're not needed for minute calculation
      return hours * 60 + minutes;
    } catch (error) {
      return 0;
    }
  };

  // Helper function to parse day ranges like "Monday - Friday" or "Monday, Wednesday"
  const parseDayRange = (dayRange) => {
    if (!dayRange) {
      return [];
    }

    const dayOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Handle single day
    if (!dayRange.includes('-') && !dayRange.includes(',')) {
      return [dayRange.trim()];
    }

    // Handle comma-separated days
    if (dayRange.includes(',') && !dayRange.includes('-')) {
      return dayRange.split(',').map(day => day.trim());
    }

    // Handle day ranges like "Monday - Friday" or "Monday - Sunday"
    if (dayRange.includes('-')) {
      const parts = dayRange.split('-').map(part => part.trim());

      if (parts.length === 2) {
        const startIndex = dayOrder.indexOf(parts[0]);
        const endIndex = dayOrder.indexOf(parts[1]);

        if (startIndex !== -1 && endIndex !== -1) {
          const days = [];

          // Special case: if start is Monday (1) and end is Sunday (0), it means Monday-Sunday (all week)
          if (parts[0] === 'Monday' && parts[1] === 'Sunday') {
            return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
          }

          // Handle normal ranges
          if (startIndex <= endIndex) {
            for (let i = startIndex; i <= endIndex; i++) {
              days.push(dayOrder[i]);
            }
          } else {
            // Handle wrap-around (e.g., Saturday - Monday)
            for (let i = startIndex; i < dayOrder.length; i++) {
              days.push(dayOrder[i]);
            }
            for (let i = 0; i <= endIndex; i++) {
              days.push(dayOrder[i]);
            }
          }

          return days;
        }
      }
    }

    return [];
  };

  const fetchOpeningHours = async () => {
    try {
      const response = await GeneralService.getOpeningHours();

      if (response && response.success && response.data) {
        if (Array.isArray(response.data) && response.data.length > 0) {
          setHoursData(response.data);

          const formattedHours = formatOpeningHours(response.data);
          setOpeningHours(formattedHours);
          setApiError(false);
        } else {
          setDefaultHours();
        }
      } else {
        setDefaultHours();
      }
    } catch (error) {
      setDefaultHours();
    }
  };

  const setDefaultHours = () => {
    setOpeningHours('Monday - Sunday: 9.00 am - 10.00 pm');
    setHoursData(null);
    setApiError(true);
  };

  const formatOpeningHours = (hours) => {
    if (!hours || !Array.isArray(hours) || hours.length === 0) {
      return 'Mon - Sat: 9.00 am - 10.00 pm, Sun: Closed';
    }

    const result = [];

    hours.forEach((hour) => {
      if (!hour || typeof hour !== 'object') {
        return;
      }

      if (hour.day_range) {
        if (hour.is_closed) {
          const status = hour.emergency_only ? 'Closed (Emergency Only)' : 'Closed';
          result.push(`${hour.day_range}: ${status}`);
        } else if (hour.formatted_time) {
          result.push(`${hour.day_range}: ${hour.formatted_time}`);
        } else if (hour.open_time && hour.close_time) {
          const openTime = formatTime(hour.open_time);
          const closeTime = formatTime(hour.close_time);
          result.push(`${hour.day_range}: ${openTime} - ${closeTime}`);
        }
      }
      else if (hour.day) {
        if (hour.is_closed) {
          const status = hour.emergency_only ? 'Closed (Emergency Only)' : 'Closed';
          result.push(`${hour.day.substring(0, 3)}: ${status}`);
        } else if (hour.open_time && hour.close_time) {
          const openTime = formatTime(hour.open_time);
          const closeTime = formatTime(hour.close_time);
          result.push(`${hour.day.substring(0, 3)}: ${openTime} - ${closeTime}`);
        }
      }
      // Handle case where there's no specific day/day_range (general hours)
      else if (hour.open_time && hour.close_time && !hour.day && !hour.day_range) {
        const openTime = formatTime(hour.open_time);
        const closeTime = formatTime(hour.close_time);
        result.push(`Daily: ${openTime} - ${closeTime}`);
      }
    });

    if (result.length === 0) {
      return 'Mon - Sat: 9.00 am - 10.00 pm, Sun: Closed';
    }

    return result.join(', ');
  };

  const formatTime = (time) => {
    if (!time || typeof time !== 'string') {
      return 'Invalid Time';
    }

    try {
      const timeParts = time.split(':');
      const hours = timeParts[0];
      const minutes = timeParts[1] || '00';

      const hourNum = parseInt(hours, 10);
      const hour12 = hourNum % 12 || 12;
      const ampm = hourNum >= 12 ? 'pm' : 'am';
      return `${hour12}.${minutes.padStart(2, '0')} ${ampm}`;
    } catch (error) {
      return 'Invalid Time';
    }
  };

  const closeMobileNotification = () => {
    setShowMobileNotification(false);
  };

  const retryFetchHours = () => {
    setOpeningHours('Loading...');
    fetchOpeningHours();
  };

  return (
    <>
      {/* Debug Info - Remove this in production */}
      {process.env.NODE_ENV === 'development' && apiError && (
        <div style={{
          background: '#ff6b6b',
          color: 'white',
          padding: '8px',
          textAlign: 'center',
          fontSize: '12px'
        }}>
          API Error: Using default hours.
          <button
            onClick={retryFetchHours}
            style={{
              marginLeft: '10px',
              padding: '2px 8px',
              background: 'white',
              color: '#ff6b6b',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Desktop Topbar */}
      <div className="container-fluid topbar-enhanced px-0 d-none d-lg-block">
        <div className="row gx-0 align-items-center">
          <div className="col-lg-7">
            <div className="opening-hours-container d-flex align-items-center">
              <span className="clock-icon-wrap">
                <i className="far fa-clock clock-icon"></i>
              </span>
              <span className="hours-text">
                Opening Hours: {openingHours}
              </span>
              <span className={`status-badge ${isOpen ? 'status-open' : 'status-closed'}`}>
                {isOpen ? '🟢 OPEN NOW' : '🔴 CLOSED'}
              </span>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="contact-info d-flex align-items-center text-white px-4">
              <div className="contact-item flex-fill text-center me-3">
                <p className="m-0">
                  <i className="fa fa-envelope-open me-2"></i>
                  <a href="mailto:zubairmukhtar14@gmail.com">zubairmukhtar14@gmail.com</a>
                </p>
              </div>
              <div className="contact-item flex-fill text-center">
                <p className="m-0">
                  <i className="fa fa-phone-alt me-2"></i>
                  <a href="tel:+923009605574">03009605574</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Notification */}
      {showMobileNotification && (
        <div className="mobile-notification d-lg-none">
          <div className="mobile-notification-content">
            <div className="mobile-notification-text">
              <i className="far fa-clock mobile-notification-icon"></i>
              {openingHours}
              <span className={`mobile-notification-status ${isOpen ? 'status-open' : 'status-closed'}`}>
                {isOpen ? '🟢 OPEN' : '🔴 CLOSED'}
              </span>
            </div>
            <button
              className="mobile-notification-close"
              onClick={closeMobileNotification}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Navbar */}
      <nav className="navbar navbar-expand-lg navbar-enhanced px-4">
        <div className="container-fluid">
          <Link to="/" className="navbar-brand">
            <div className="logo-container">
              <img src={azmLogo} alt="AZM Logo" className="logo-img" />
              <h1 className="brand-text d-none d-sm-block">Azm Rehab Centre</h1>
            </div>
          </Link>

          {/* Custom Navbar Toggler with manual control */}
          <button
            className="navbar-toggler"
            type="button"
            onClick={toggleNavbar}
            aria-controls="navbarCollapse"
            aria-expanded={isNavbarOpen}
            aria-label="Toggle navigation"
          >
            {/* Custom hamburger icon */}
            <div className="hamburger-icon">
              <span className={`bar ${isNavbarOpen ? 'bar-1-active' : ''}`}></span>
              <span className={`bar ${isNavbarOpen ? 'bar-2-active' : ''}`}></span>
              <span className={`bar ${isNavbarOpen ? 'bar-3-active' : ''}`}></span>
            </div>
          </button>

          {/* Navbar collapse with manual control */}
          <div className={`collapse navbar-collapse ${isNavbarOpen ? 'show' : ''}`} id="navbarCollapse">
            <div className="navbar-nav ms-auto align-items-lg-center">
              <Link to="/" className={`nav-item nav-link ${isActive('/') ? 'active' : ''}`} onClick={closeNavbar}>Home</Link>
              <Link to="/service" className={`nav-item nav-link ${isActive('/service') ? 'active' : ''}`} onClick={closeNavbar}>Services</Link>
              <Link to="/staff-team" className={`nav-item nav-link ${isActive('/staff-team') ? 'active' : ''}`} onClick={closeNavbar}>Staff</Link>
              <Link to="/contact-us" className={`nav-item nav-link ${isActive('/contact-us') ? 'active' : ''}`} onClick={closeNavbar}>Contact</Link>
              <Link to="/#appointment-form" className="btn-book-appointment" onClick={handleBookAppointment}>
                <i className="fas fa-calendar-check me-2"></i>Book Appointment
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}
